// The kiosk (design/screens/kiosk-details.html, kiosk-medical-aid.html, kiosk.html; UC-PATIENTS-022, 023; UC-GOVERNANCE-013):
// a tablet in patient mode. No shell, no navigation beyond the three steps; the staff exit needs the Control PIN
// (`DELETE /auth/handover` through the step-up modal, action HandoverExit); the kiosk's own idle timer discards the form and
// locks the screen. A new patient's answers travel in one registration submission under the handover token; an existing
// patient's consent is captured at once (`POST /patients/{id}/consents`, FIXTURE's 08:22) and the details follow as an
// UpdateExisting submission for reception to approve. The thank-you screen then waits for staff.
//
// A new patient's consent step shows the handover answer's form — the practice's wording, the Owner's defaults, the consent
// text and the privacy contact. If that wording is replaced while the form is open, the submission is refused as stale and no
// route under the token can serve the new text for a patient without a record, so the kiosk asks for the device back and
// staff restart patient mode (UC-PATIENTS-023 E1); an existing patient's step re-reads its consent form instead.
import { CONSENT_PURPOSES, consentFormPurposes, type ConsentFormView, type ConsentPurpose, type HandoverForm, type SessionView } from "@visionx/contracts";
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { consent as consentApi, handover, registration } from "../api/endpoints.consent";
import { describeError, isApiError, isNetworkError } from "../api/problem";
import { formatDate } from "../lib/format";
import { buildConsentInput, buildSubmission, emptyForm, errorKeyOf, isMinorOf, stepOfIssue, validateConsent, validateDetails, validateMedicalAid, type Errors, type KioskForm } from "../kiosk/form";
import { useKioskIdle, useKioskKeepAlive } from "../kiosk/idle";
import { abandonKiosk, enterKiosk, exitKiosk, getKioskState, markKioskSubmitted, notInHandover, type KioskState } from "../kiosk/mode";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert } from "../ui/bits";
import { Icon } from "../ui/icons";
import { KioskConsent, type Purpose } from "./KioskConsent";
import { KioskDetails } from "./KioskDetails";
import { KioskMedicalAid } from "./KioskMedicalAid";

type Step = 1 | 2 | 3 | 4;

/** The handover form's purposes in the consent step's shape; null when the kiosk state carries no form (a state stored before it). */
function handedPurposes(form: HandoverForm | null | undefined): Purpose[] | null {
  const known = (form?.purposes ?? []).filter((p) => (CONSENT_PURPOSES as readonly string[]).includes(p.purpose));
  return known.length > 0 ? known.map((p) => ({ purpose: p.purpose as ConsentPurpose, label: p.label, description: p.description, required: p.required, preTicked: p.preTicked })) : null;
}

/** The consent wording the answers were given against is no longer current: `409 VERSION_CONFLICT`, or the submission's `422` on `consent.textVersion`. */
const staleWording = (err: unknown): boolean =>
  isApiError(err) && (err.code === "VERSION_CONFLICT" || (err.code === "VALIDATION_FAILED" && err.errors.some((f) => f.path === "consent.textVersion")));

/** How long the staff exit button is held before the PIN prompt opens — a patient's tap does nothing. */
export const HOLD_TO_EXIT_MS = 1_200;

/** The staff exit failed after the prompt (or offline, before it could open): the kiosk stays and says why (UC-PATIENTS-022 E7). */
const exitProblem = (err: unknown): ReactNode => (
  <Alert tone="crit"><b>Could not leave patient mode.</b> {isNetworkError(err) && err.offline ? "This device is offline — try again once the connection is back." : describeError(err)}</Alert>
);

export function Kiosk({ state, session }: { state: KioskState; session: SessionView }) {
  const navigate = useNavigate();
  const withStepUp = useStepUp();
  // D13: the kiosk runs outside the shell, so it says itself when nothing can be sent, and "I agree and sign" goes quiet.
  const block = useWriteBlock();
  const consentOnly = state.formType === "Consent";
  const done = state.submissionId !== null;
  // The practice's wording and the Owner's defaults from the handover; the platform's D43 definitions only for a state without them.
  const defaults = useMemo<readonly Purpose[]>(() => handedPurposes(state.form) ?? consentFormPurposes(null, state.practiceName, null), [state.form, state.practiceName]);
  const [form, setForm] = useState<KioskForm>(() => emptyForm(state.prefill, { purposes: defaults, textVersion: state.form?.consentText?.version ?? state.consentTextVersion }));
  const [step, setStep] = useState<Step>(done ? 4 : consentOnly ? 3 : 1);
  const [errors, setErrors] = useState<Errors>({});
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const [busy, setBusy] = useState(false);
  const [consentForm, setConsentForm] = useState<ConsentFormView | null>(null);
  const [formReload, setFormReload] = useState(0);
  // A new patient's wording was replaced mid-form: nothing under the token can show the new text, so staff restart patient mode.
  const [wordingStale, setWordingStale] = useState(false);
  // The consent form's wording and defaults are applied once per read, not on every visit to the step, so the ticks survive Back.
  const consentApplied = useRef<string | null>(null);
  const [submissionKey] = useState(() => newIdempotencyKey());
  // An existing patient's consent capture (UC-GOVERNANCE-013): one Idempotency-Key per distinct body, so pressing sign again
  // after a lost answer replays the same capture (the API lets a retry under its own key through, TC-PATIENTS-147) while a
  // re-read wording makes a new request (a reused key with another body is 409 IDEMPOTENCY_KEY_REUSED). A handover takes one
  // capture (D62): once it has landed the choices are locked on this screen, and a retry after a refused submission sends the
  // details alone (TC-PATIENTS-131, TC-PATIENTS-146).
  const consentAttempt = useRef<{ body: string; key: string } | null>(null);
  const [recorded, setRecordedState] = useState<{ id: string; at: string } | null>(null);
  // The same, read by the handlers of the sign() call that recorded it (the state is the render's).
  const recordedRef = useRef<{ id: string; at: string } | null>(null);
  const setRecorded = (r: { id: string; at: string }) => { recordedRef.current = r; setRecordedState(r); };
  const isMinor = consentForm?.patient.isMinor ?? isMinorOf(form.details);
  // An adopted consent-only handover has no pre-fill: the consent form's patient block names the patient instead.
  const firstName = (form.details.firstNames.trim() || consentForm?.patient.displayName.trim() || "").split(/\s+/)[0] ?? "";

  useKioskIdle(state.idleMinutes, () => { void abandonKiosk(); });
  // BR-PATIENTS-028: while the patient is at the tablet the handover stays open on the server too. A lapsed handover locks the
  // session (401, followed by the lock screen); one the server no longer holds at all (409 not_in_handover — staff left patient
  // mode elsewhere) locks the screen here, as the exit does (A8): the tablet is in a patient's hands. Only for the kiosk that sent
  // the signal: an answer arriving after staff left it, or moved on to the next patient, says nothing of the kiosk now shown
  // (review, 4 Oct 2026).
  useKioskKeepAlive(() => {
    const sentFor = state.enteredAt;
    void handover.activity().catch((err: unknown) => { if (notInHandover(err) && getKioskState()?.enteredAt === sentFor) void abandonKiosk(); });
  }, block === null);

  // The submission may have landed before a reload: the kiosk opens on the thank-you screen and nothing else.
  useEffect(() => { if (done) setStep(4); }, [done]);
  // PAT-73: a patient under 18 — from the details typed, or from the consent form — is signed for by a guardian; the patient's
  // own name is not the guardian's. A date of birth corrected to an adult's (a mistyped ID) puts the patient back as the signer
  // and drops the guardian block, which the consent step no longer shows (UC-PATIENTS-023 A2; UC-GOVERNANCE-013 E3).
  useEffect(() => {
    setForm((f) => {
      if (isMinor) return f.consent.signerType === "Guardian" ? f : { ...f, consent: { ...f.consent, signerType: "Guardian", signerName: "" } };
      if (f.consent.signerType !== "Guardian") return f;
      const own = `${f.details.firstNames.trim()} ${f.details.surname.trim()}`.trim() || consentForm?.patient.displayName.trim() || "";
      return { ...f, consent: { ...f.consent, signerType: "Self", signerName: own, guardianIdNumber: "", guardianRelationship: "", guardianPatientId: null } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinor]);
  // The pad starts empty whenever the consent step opens, so a signature kept from an earlier visit (Back, or a 422 that sent
  // the patient to an earlier step) is dropped with it: what is sent is always what the patient signed on the screen they saw.
  useEffect(() => {
    if (step === 3 || step === 4) return;
    setForm((f) => (f.consent.signaturePng === null && f.consent.signedAt === null ? f : { ...f, consent: { ...f.consent, signaturePng: null, signedAt: null } }));
  }, [step]);

  const focusFirst = (e: Errors) => {
    const first = Object.keys(e)[0];
    if (first) setTimeout(() => document.getElementById(`k-${first}`)?.focus(), 0);
  };

  const next = () => {
    setNotice(null);
    const e = step === 1 ? validateDetails(form.details) : step === 2 ? validateMedicalAid(form.medicalAid) : {};
    setErrors(e);
    if (Object.keys(e).length > 0) { focusFirst(e); return; }
    setStep((s) => (s === 1 ? 2 : 3));
  };
  // The signer defaults to the patient's own name as typed on step 1 when the consent step opens (BR-GOVERNANCE-020).
  useEffect(() => {
    if (step !== 3) return;
    setForm((f) => (f.consent.signerType === "Self" && f.consent.signerName.trim() === "" ? { ...f, consent: { ...f.consent, signerName: `${f.details.firstNames.trim()} ${f.details.surname.trim()}`.trim() } } : f));
  }, [step]);
  const back = () => { setErrors({}); setNotice(null); setStep((s) => (s === 3 ? 2 : 1)); };

  const failed = (err: unknown) => {
    if (isNetworkError(err) && err.offline) {
      setNotice(<Alert tone="crit"><b>Not sent — this device is offline.</b> Please hand it back to reception.</Alert>);
      return;
    }
    if (isApiError(err) && err.code === "PERMISSION_DENIED" && typeof err.meta.reason === "string" && err.meta.reason.startsWith("handover")) {
      setNotice(<Alert tone="crit"><b>This form can no longer be sent.</b> Please hand the device back to reception.</Alert>);
      return;
    }
    // The consent wording changed after the form opened (UC-PATIENTS-023 E1): the signature belongs to the old text.
    if (staleWording(err)) {
      setErrors({});
      setStep(3);
      setForm((f) => ({ ...f, consent: { ...f.consent, signaturePng: null, signedAt: null } }));
      if (state.patientId && !recordedRef.current) {
        // An existing patient's consent form is reachable under the token: read the wording in force and ask again.
        setFormReload((n) => n + 1);
        setNotice(<Alert tone="warn"><b>The consent wording changed while you were reading.</b> Please read the new version and sign again.</Alert>);
      } else {
        // A new patient's new wording cannot be read under the token; an existing patient's one capture of this handover is
        // spent on the old wording (D62). Either way staff restart patient mode, which carries the new wording.
        setNotice(null);
        setWordingStale(true);
      }
      return;
    }
    if (isApiError(err) && err.code === "VALIDATION_FAILED" && err.errors.length > 0) {
      const e: Errors = {};
      let target: Step = 3;
      for (const f of err.errors) { e[errorKeyOf(f.path)] = f.message; target = Math.min(target, stepOfIssue(f.path)) as Step; }
      setErrors(e);
      setStep(consentOnly ? 3 : target);
      setNotice(<Alert tone="crit"><b>Something needs a correction.</b> {err.errors.map((f) => f.message).join(" ")}</Alert>);
      focusFirst(e);
      return;
    }
    setNotice(<Alert tone="crit"><b>Could not send.</b> {describeError(err)}</Alert>);
  };

  const sign = async () => {
    setNotice(null);
    const e = validateConsent(form.consent, isMinor);
    setErrors(e);
    if (Object.keys(e).length > 0) { focusFirst(e); return; }
    setBusy(true);
    try {
      let recordId: string | null = recordedRef.current?.id ?? null;
      if (state.patientId && !recordedRef.current) {
        const built = buildConsentInput(form.consent);
        if (!built.ok) { setErrors(Object.fromEntries(built.issues.map((i) => [errorKeyOf(i.path), i.message]))); return; }
        try {
          const body = JSON.stringify(built.value);
          if (consentAttempt.current?.body !== body) consentAttempt.current = { body, key: newIdempotencyKey() };
          recordId = (await consentApi.capture(state.patientId, built.value, consentAttempt.current.key, state.token)).data.id;
          setRecorded({ id: recordId, at: new Date().toISOString() });
        } catch (err) {
          if (isApiError(err) && err.code === "VERSION_CONFLICT") {
            const current = typeof err.meta.currentTextVersion === "number" ? err.meta.currentTextVersion : null;
            setForm((f) => ({ ...f, consent: { ...f.consent, textVersion: current, signaturePng: null, signedAt: null } }));
            setFormReload((n) => n + 1);
            setNotice(<Alert tone="warn"><b>The consent wording changed while you were reading.</b> Please read version {current ?? "—"} and sign again.</Alert>);
            return;
          }
          throw err;
        }
      }
      if (!consentOnly) {
        const built = buildSubmission(form, state);
        if (!built.ok) {
          const e2: Errors = {};
          let target: Step = 3;
          for (const i of built.issues) { e2[errorKeyOf(i.path)] = i.message; target = Math.min(target, stepOfIssue(i.path)) as Step; }
          setErrors(e2);
          setStep(target);
          setNotice(<Alert tone="crit"><b>Something needs a correction.</b> {built.issues.map((i) => i.message).join(" ")}</Alert>);
          focusFirst(e2);
          return;
        }
        const { data } = await registration.submit(built.value, submissionKey, state.token);
        markKioskSubmitted(data.id);
      } else {
        markKioskSubmitted(recordId ?? "consent");
      }
      setStep(4);
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  };

  // One Idempotency-Key per exit attempt, minted outside the step-up retry so the refused call and the retry share it.
  const exit = useCallback(async () => {
    const key = newIdempotencyKey();
    try {
      const r = await withStepUp((g) => exitKiosk(g, key), { title: "Exit patient mode", description: "Leaving patient mode returns this device to the staff screens.", reason: "none" });
      // Locked instead (A8): the lock screen stands here, and the unlock returns to the staff shell.
      if (r !== undefined && !r.locked) navigate(state.returnTo, { replace: true });
    } catch (err) {
      setNotice(exitProblem(err));
    }
  }, [withStepUp, navigate, state.returnTo]);

  // The PIN exit, then exactly one new handover: the kiosk route starts nothing itself after a kiosk ended in it. Next patient
  // starts a new registration; "Restart patient mode" after the wording changed (`again`) starts the same handover — the same
  // kind, for the same patient — which carries the new wording (an update of Thandiwe's record stays hers, review W13).
  const nextPatient = useCallback(async (prompt: { title: string; description: string } = { title: "Next patient", description: "Leaving patient mode, then starting a new registration on this device." }, again = false) => {
    const key = newIdempotencyKey();
    let r: Awaited<ReturnType<typeof exitKiosk>> | undefined;
    try {
      r = await withStepUp((g) => exitKiosk(g, key), { ...prompt, reason: "none" });
    } catch (err) {
      setNotice(exitProblem(err));
      return;
    }
    if (r === undefined || r.locked) return;
    const entry = again ? { formType: state.formType, ...(state.patientId ? { patientId: state.patientId } : {}) } : { formType: "Registration" as const };
    try { await enterKiosk({ ...entry, returnTo: state.returnTo, session }); }
    catch { navigate(state.returnTo, { replace: true }); }
  }, [withStepUp, navigate, state.returnTo, state.formType, state.patientId, session]);

  // The effective date of the wording the handover named, while it is the version on the form.
  const handedText = state.form?.consentText ?? null;
  const handedEffective = handedText && handedText.version === form.consent.textVersion ? handedText.effectiveFrom : null;

  const steps: { n: number; label: string; key: Step }[] = consentOnly
    ? [{ n: 1, label: "Consent", key: 3 }, { n: 2, label: "Done", key: 4 }]
    : [{ n: 1, label: "Your details", key: 1 }, { n: 2, label: "Medical aid", key: 2 }, { n: 3, label: "Consent", key: 3 }, { n: 4, label: "Done", key: 4 }];

  return (
    <section className="screen" id="s-kiosk" data-bare="true">
      <div className="kiosk-wrap">
        <div className="kiosk" role="main" aria-label="Patient registration">
          <div className="khead">
            <div className="kwelcome">Welcome to {state.practiceName}{state.branchName ? ` ${state.branchName}` : ""}</div>
            <ol className="steps right" aria-label="Steps">
              {steps.map((s) => (
                <li key={s.key} className={`st${s.key === step ? " cur" : ""}`} aria-current={s.key === step ? "step" : undefined}>
                  <span className={`s${s.key < step ? " done" : s.key === step ? " cur" : ""}`} aria-hidden="true">{s.key < step ? <Icon name="check" width={14} height={14} strokeWidth={2.2} /> : s.n}</span>{s.label}
                </li>
              ))}
            </ol>
          </div>

          <div className="kbody">
            {notice}
            {wordingStale && step !== 4 && (
              <Alert tone="warn" role="alert" actions={<button type="button" className="btn" onClick={() => void nextPatient({ title: "Restart patient mode", description: "Leaving patient mode, then starting the registration again with the practice's current consent wording." }, true)}><Icon name="lock" />Restart patient mode</button>}>
                <b>The consent wording was updated while this form was open.</b> Please hand the device back to reception. Staff: restart patient mode to show the new wording — the answers on this form are not kept.
              </Alert>
            )}
            {form.consent.textVersion === null && !wordingStale && step !== 4 && (
              <Alert tone="warn" role="alert"><b>This practice has not published its consent wording yet, so this form cannot be sent.</b> Please hand the device back to reception. Staff: publish the consent text in Settings › General › Consent text, then start patient mode again.</Alert>
            )}
            {block && step !== 4 && (
              <Alert tone="warn"><b>{block === "offline" ? "This device is offline." : "This practice is read-only for now."}</b> {block === "offline" ? "Your answers stay on this screen but cannot be sent until the connection is back. If it does not come back, please hand the device to reception." : "Your answers cannot be sent. Please hand the device back to reception."}</Alert>
            )}
            {step === 1 && (
              <KioskDetails value={form.details} errors={errors} titles={state.titles} prefill={state.prefill} onChange={(details) => setForm((f) => ({ ...f, details }))} />
            )}
            {step === 2 && (
              <KioskMedicalAid value={form.medicalAid} errors={errors} prefill={state.prefill} onChange={(medicalAid) => setForm((f) => ({ ...f, medicalAid }))} onNoMedicalAid={() => { setForm((f) => ({ ...f, medicalAid: { ...f.medicalAid, hasMedicalAid: false } })); setErrors({}); setStep(3); }} />
            )}
            {step === 3 && (
              <KioskConsent value={form.consent} errors={errors} state={state} isMinor={isMinor} defaults={defaults} reloadKey={formReload} form={consentForm} applied={consentApplied} recordedAt={recorded?.at ?? null}
                onFormLoaded={(f) => { setConsentForm(f); }}
                onChange={(consent) => setForm((f) => ({ ...f, consent }))} />
            )}
            {step === 4 && (
              <div className="kdone">
                <h2 className="ctitle">Thank you{firstName ? `, ${firstName}` : ""} — please hand the device back to reception.</h2>
                <div className="muted">
                  {consentOnly ? "Your consent has been recorded on your patient record." : state.patientId ? "Your consent has been recorded and your updated details are with reception for checking." : "Your details are with reception for checking; they will finish your registration at the desk."}
                </div>
                <div className="row c gap-8 kstaff">
                  <span className="help">Staff:</span>
                  <button type="button" className="btn" onClick={() => void nextPatient()}><Icon name="lock" />Next patient</button>
                  <button type="button" className="btn" onClick={() => void exit()}><Icon name="lock" />Return to {state.patientId ? "the record" : "Today"}</button>
                </div>
              </div>
            )}

            {step !== 4 && (
              <div className="row c kfootrow">
                {step === 1 || (consentOnly && step === 3)
                  ? <button className="btn" type="button" disabled>Back</button>
                  : <button className="btn" type="button" onClick={back} disabled={busy}>Back</button>}
                <span className="grow cd">
                  {step === 1 && "Fields marked * are required. Your details are kept under POPIA — ask reception for a copy at any time."}
                  {step === 2 && "Sharing with your medical aid for claims is one of the choices on the next screen."}
                  {step === 3 && (
                    consentForm?.privacyNotice
                      ? <>Privacy notice version {consentForm.privacyNotice.version} · published {formatDate(consentForm.privacyNotice.publishedAt ?? `${consentForm.privacyNotice.effectiveFrom}T00:00:00.000Z`)}</>
                      : form.consent.textVersion !== null ? <>Consent wording version {form.consent.textVersion}{handedEffective ? ` · effective ${formatDate(`${handedEffective}T00:00:00.000Z`)}` : ""}</> : <>No consent wording is published yet</>
                  )}
                </span>
                {step === 3
                  ? <button className="btn primary" type="button" disabled={busy || form.consent.textVersion === null || block !== null || wordingStale} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void sign()}>{busy ? "Sending…" : "I agree and sign"}</button>
                  : <button className="btn primary" type="button" onClick={next}>Next</button>}
              </div>
            )}
          </div>

          <div className="kfoot">
            <Icon name="lock" width={18} height={18} />
            <span className="muted kfoot-note">This device is in patient mode. Hand it back to staff when you are done.</span>
            <HoldToExit onExit={() => void exit()} />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * "Staff: hold to exit — Control PIN": a press held for HOLD_TO_EXIT_MS opens the PIN prompt; a tap does nothing; Enter or Space
 * (a keyboard click) opens it at once. The button keeps its size while held — "Keep holding…" shows in the space its own longer
 * words keep — and the press captures the pointer, so a mouse held still anywhere on it completes the hold. Swapping the words
 * used to shrink the right-aligned button out from under a mouse held on its left part, and the pointerleave that followed
 * cancelled the hold (DVN's test checklist of Sprints 1 and 2, item D2); touch captures the pointer by itself and was not affected.
 */
export function HoldToExit({ onExit }: { onExit: () => void }) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [holding, setHolding] = useState(false);
  const cancel = () => { if (timer.current !== undefined) clearTimeout(timer.current); timer.current = undefined; setHolding(false); };
  const start = (e: ReactPointerEvent<HTMLButtonElement>) => {
    cancel();
    // Until it is released the press is the button's, wherever the pointer drifts; where it cannot be captured, leaving still cancels.
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not captured: pointerleave cancels as it always did */ }
    setHolding(true);
    timer.current = setTimeout(() => { timer.current = undefined; setHolding(false); onExit(); }, HOLD_TO_EXIT_MS);
  };
  useEffect(() => () => { if (timer.current !== undefined) clearTimeout(timer.current); }, []);
  return (
    <button type="button" className={`btn ghost right kexit${holding ? " holding" : ""}`} aria-label="Staff: hold to exit — Control PIN"
      onPointerDown={start} onPointerUp={cancel} onPointerLeave={cancel} onPointerCancel={cancel} onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => { if (e.detail === 0) onExit(); }}>
      <Icon name="lock" />
      <span className="kexit-label">
        <span className={holding ? "off" : undefined}>Staff: hold to exit — Control PIN</span>
        <span className={holding ? undefined : "off"}>Keep holding…</span>
      </span>
    </button>
  );
}
